/**
 * TicketDialog — reusable ticket create/edit modal.
 *
 * Usage:
 *   const { openTicketDialog } = useTicketDialog();
 *   openTicketDialog();                       // blank new ticket
 *   openTicketDialog({ project_id: 123 });    // prefilled from a project
 *   openTicketDialog({ ticket_id: 456 });     // edit existing
 *
 * Rules of the road:
 *   - Title is the ONLY field we require. Everything else auto-populates from
 *     /api/tickets/lookup/context the moment a client/project/job is picked.
 *   - Single combobox searches projects + contacts in one call.
 *   - Once a project is selected, job #, AR code, site address, client name,
 *     primary contact (if any), and a suggested department all fill in.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Stack, Chip, Typography, MenuItem, IconButton,
  Autocomplete, CircularProgress, Divider, Tooltip, Alert, Grid,
  useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import WorkIcon from '@mui/icons-material/Work';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import ApartmentIcon from '@mui/icons-material/Apartment';
import axios from 'axios';

const api = axios.create({ baseURL: '' });
api.interceptors.request.use((c) => {
  const t = localStorage.getItem('token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const TicketDialogContext = createContext(null);

export const useTicketDialog = () => {
  const ctx = useContext(TicketDialogContext);
  if (!ctx) throw new Error('useTicketDialog must be used within TicketDialogProvider');
  return ctx;
};

// ── Helpers ────────────────────────────────────────────────────────────────
// Mike Murray, 2026-04-24: default due dates by priority (client-side mirror
// of server/routes/tickets.js DEFAULT_DUE_HOURS_BY_PRIORITY).
const DEFAULT_DUE_HOURS = { Low: 14 * 24, Normal: 7 * 24, High: 24, Urgent: 4 };
const defaultDueAtFor = (priority) => {
  const hrs = DEFAULT_DUE_HOURS[priority];
  if (!hrs) return '';
  const d = new Date();
  d.setHours(d.getHours() + hrs);
  // datetime-local input wants YYYY-MM-DDTHH:MM (no seconds, no tz).
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const EMPTY_FORM = {
  title: '', description: '',
  // Default ticket_type is null — the Zoho catalog requires an explicit pick.
  // Status is always 'New' on create (the field is hidden from the creator).
  status: 'New', priority: 'Normal', ticket_type: '', department: '', subtype: '',
  project_id: null, job_number: '', ar_code: '', engagement_id: null,
  client_name: '', contact_zoho_id: null, contact_name: '', contact_email: '', contact_phone: '',
  site_address: '', assigned_to: null, agent_id: null, due_at: '',
  // metadata holds the department-specific extra fields keyed by field.key
  metadata: {},
};

// Label for the smart search combobox — used by MUI Autocomplete
const renderOptionLabel = (opt) => {
  if (!opt) return '';
  return `${opt.label || ''}${opt.sublabel ? ' — ' + opt.sublabel : ''}`;
};

// Icon + chip color + display label per search-result kind
const KIND_META = {
  client:     { icon: BusinessIcon,  color: 'primary',   label: 'Client'     },
  project:    { icon: WorkIcon,      color: 'secondary', label: 'Project'    },
  engagement: { icon: ApartmentIcon, color: 'info',      label: 'Engagement' },
  contact:    { icon: PersonIcon,    color: 'default',   label: 'Contact'    },
};

function PriorityChip({ value }) {
  const color = value === 'Urgent' ? 'error' : value === 'High' ? 'warning' : value === 'Low' ? 'default' : 'info';
  return <Chip size="small" label={value} color={color} />;
}

// ── Provider ───────────────────────────────────────────────────────────────
export function TicketDialogProvider({ children, onTicketCreated }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('create'); // 'create' | 'edit'
  const [prefill, setPrefill] = useState({});
  const [ticketId, setTicketId] = useState(null);

  const openTicketDialog = useCallback((opts = {}) => {
    if (opts.ticket_id) {
      setMode('edit');
      setTicketId(opts.ticket_id);
      setPrefill({});
    } else {
      setMode('create');
      setTicketId(null);
      setPrefill(opts || {});
    }
    setOpen(true);
  }, []);

  const close = () => setOpen(false);

  return (
    <TicketDialogContext.Provider value={{ openTicketDialog }}>
      {children}
      {open && (
        <TicketDialogInner
          mode={mode}
          ticketId={ticketId}
          prefill={prefill}
          onClose={close}
          onCreated={(t) => {
            if (onTicketCreated) onTicketCreated(t);
            // Broadcast so any open list/dashboard can refetch without a direct prop.
            window.dispatchEvent(new CustomEvent('ticket:updated', { detail: t }));
            close();
          }}
        />
      )}
    </TicketDialogContext.Provider>
  );
}

// ── Dialog component (internal) ────────────────────────────────────────────
function TicketDialogInner({ mode, ticketId, prefill, onClose, onCreated }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [meta, setMeta]         = useState({
    statuses: [], priorities: [], ticket_types: [],
    ticket_type_options: [], ticket_type_groups: [],
    departments: [], department_configs: [],
  });
  const [form, setForm]         = useState(() => ({
    ...EMPTY_FORM,
    // Seed default due date on first open so the field isn't empty.
    // Priority default is 'Normal' (7 days). If the user changes priority,
    // we recompute (see Priority onChange) — unless they've manually edited
    // the due date, in which case we leave it alone.
    due_at: defaultDueAtFor('Normal'),
  }));
  const [dueAtTouched, setDueAtTouched] = useState(false);
  const [users, setUsers]       = useState([]);
  const [deptConfig, setDeptConfig] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [loading, setLoading]   = useState(mode === 'edit');
  const [error, setError]       = useState('');
  const [linkedBanner, setLinkedBanner] = useState(null);

  // Search state
  const [searchInput, setSearchInput] = useState('');
  const [searchOpts, setSearchOpts]   = useState([]);
  const [searching, setSearching]     = useState(false);

  // Sites known for the current client (Mike Murray request, 2026-04-24):
  // when a client has multiple active sites, show a dropdown restricted to that
  // client's addresses instead of free-text entry.
  const [clientSites, setClientSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);

  // Initial loads ───────────────────────────────────────────────────────────
  useEffect(() => {
    api.get('/api/tickets/meta').then(r => setMeta(r.data)).catch(() => {});
    // Source the assignment dropdowns from the `users` table — the same id
    // space ticket FKs (assigned_to, agent_id, created_by) reference.
    // (`/api/directory` is the directory_employees catalog and uses a DIFFERENT
    // id space — sourcing from it caused tickets to be assigned to the wrong
    // people. Bug fix 2026-05-20.)
    // include_self=1 because the current user IS a valid assignee on a ticket.
    api.get('/api/messaging/users?include_self=1')
      .then(r => {
        const raw = Array.isArray(r.data) ? r.data : [];
        const normalized = raw
          .map(u => ({
            id: u.id,
            name: u.name || u.email || `User ${u.id}`,
            role: u.role || '',
            email: u.email,
          }))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setUsers(normalized);
      })
      .catch(() => {});
  }, []);

  // Load the client's known site addresses whenever ar_code changes. If only
  // one site exists we silently auto-fill site_address; multiple sites surface
  // a dropdown restricted to that client.
  useEffect(() => {
    if (!form.ar_code) { setClientSites([]); return; }
    let cancelled = false;
    setSitesLoading(true);
    api.get(`/api/tickets/lookup/sites?ar_code=${encodeURIComponent(form.ar_code)}`)
      .then(r => {
        if (cancelled) return;
        const sites = Array.isArray(r.data?.sites) ? r.data.sites : [];
        setClientSites(sites);
        // If exactly one site and no site_address yet (or it equals that one),
        // auto-fill. Never overwrite a user-typed override.
        if (sites.length === 1) {
          setForm(f => {
            if (!f.site_address || f.site_address.trim() === '') {
              return { ...f, site_address: sites[0].site_address };
            }
            return f;
          });
        }
      })
      .catch(() => { if (!cancelled) setClientSites([]); })
      .finally(() => { if (!cancelled) setSitesLoading(false); });
    return () => { cancelled = true; };
  }, [form.ar_code]);

  // Load department config whenever the selected department changes.
  // This gives us the subtypes dropdown + any extra dynamic fields.
  useEffect(() => {
    if (!form.department) { setDeptConfig(null); return; }
    // Check meta.department_configs first (already loaded, single round-trip).
    const cached = (meta.department_configs || []).find(d => d.key === form.department || d.label === form.department);
    if (cached) { setDeptConfig(cached); return; }
    // Fallback: fetch individually.
    api.get(`/api/tickets/departments/${encodeURIComponent(form.department)}`)
      .then(r => setDeptConfig(r.data))
      .catch(() => setDeptConfig(null));
  }, [form.department, meta.department_configs]);

  // Load ticket (edit mode)
  useEffect(() => {
    if (mode !== 'edit' || !ticketId) return;
    setLoading(true);
    api.get(`/api/tickets/${ticketId}`)
      .then(r => {
        const t = r.data.ticket;
        setForm({
          title: t.title || '',
          description: t.description || '',
          status: t.status || 'New',
          priority: t.priority || 'Normal',
          ticket_type: t.ticket_type || '',
          department: t.department || '',
          subtype: t.subtype || '',
          metadata: t.metadata || {},
          project_id: t.project_id,
          job_number: t.job_number || '',
          ar_code: t.ar_code || '',
          engagement_id: t.engagement_id,
          client_name: t.client_name || '',
          contact_zoho_id: t.contact_zoho_id || null,
          contact_name: t.contact_name || '',
          contact_email: t.contact_email || '',
          contact_phone: t.contact_phone || '',
          site_address: t.site_address || '',
          assigned_to: t.assigned_to || null,
          agent_id: t.agent_id || null,
          due_at: t.due_at ? String(t.due_at).slice(0, 16) : '',
          _ticket_number: t.ticket_number,
        });
        // Edit mode: the existing due_at is user-authored — treat as touched
        // so changing priority here doesn't silently overwrite it.
        setDueAtTouched(true);
      })
      .catch(e => setError(e?.response?.data?.error || 'Failed to load ticket'))
      .finally(() => setLoading(false));
  }, [mode, ticketId]);

  // Apply prefill (from opening on a project page, etc.)
  useEffect(() => {
    if (mode !== 'create' || !prefill) return;
    const params = new URLSearchParams();
    if (prefill.project_id) params.set('project_id', prefill.project_id);
    if (prefill.ar_code)    params.set('ar_code', prefill.ar_code);
    if (prefill.job_number) params.set('job_number', prefill.job_number);
    if (prefill.zoho_id)    params.set('zoho_id', prefill.zoho_id);
    if (params.toString()) {
      // Prefill from a project/etc page: don't reset user-provided fields.
      api.get(`/api/tickets/lookup/context?${params.toString()}`)
        .then(r => applyContext(r.data, 'Prefilled from context', { isReset: false }))
        .catch(() => {});
    }
    // Also apply raw prefill like initial title / department
    setForm(f => ({
      ...f,
      ...(prefill.title ? { title: prefill.title } : {}),
      ...(prefill.department ? { department: prefill.department } : {}),
      ...(prefill.ticket_type ? { ticket_type: prefill.ticket_type } : {}),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, JSON.stringify(prefill || {})]);

  // Search debounce
  useEffect(() => {
    if (!searchInput || searchInput.length < 2) { setSearchOpts([]); return; }
    const h = setTimeout(() => {
      setSearching(true);
      api.get(`/api/tickets/lookup/search?q=${encodeURIComponent(searchInput)}`)
        .then(r => setSearchOpts(r.data.results || []))
        .catch(() => setSearchOpts([]))
        .finally(() => setSearching(false));
    }, 150);
    return () => clearTimeout(h);
  }, [searchInput]);

  // Apply an auto-populate context response.
  //
  // Mike Murray, 2026-04-24: when a user picks a new client/project/contact,
  // every linked field should be **replaced** with the new context (not merged
  // with whatever was there before). Previously we used ??-merge which left
  // stale values behind (e.g., contact from a prior search sticking after the
  // user picked a new client). The one exception is the `prefill` path — when
  // the dialog is opened from a project page, applyContext runs with
  // isReset=false so we preserve whatever the caller pre-populated.
  const applyContext = (ctx, bannerMsg, { isReset = true } = {}) => {
    setForm(f => {
      if (isReset) {
        return {
          ...f,
          project_id:      ctx.project_id      ?? null,
          job_number:      ctx.job_number      ?? '',
          ar_code:         ctx.ar_code         ?? '',
          engagement_id:   ctx.engagement_id   ?? null,
          client_name:     ctx.client_name     ?? '',
          contact_zoho_id: ctx.contact_zoho_id ?? null,
          contact_name:    ctx.contact_name    ?? '',
          contact_email:   ctx.contact_email   ?? '',
          contact_phone:   ctx.contact_phone   ?? '',
          site_address:    ctx.site_address    ?? '',
          department:      f.department || ctx.suggested_department || '',
          _project_name:   ctx.project_name     ?? '',
        };
      }
      // Merge path — used by prefill (opening dialog from a project page).
      return {
        ...f,
        project_id:      ctx.project_id      ?? f.project_id,
        job_number:      ctx.job_number      ?? f.job_number,
        ar_code:         ctx.ar_code         ?? f.ar_code,
        engagement_id:   ctx.engagement_id   ?? f.engagement_id,
        client_name:     ctx.client_name     ?? f.client_name,
        contact_zoho_id: ctx.contact_zoho_id ?? f.contact_zoho_id,
        contact_name:    ctx.contact_name    ?? f.contact_name,
        contact_email:   ctx.contact_email   ?? f.contact_email,
        contact_phone:   ctx.contact_phone   ?? f.contact_phone,
        site_address:    ctx.site_address    ?? f.site_address,
        department:      f.department || ctx.suggested_department || '',
        _project_name:   ctx.project_name || f._project_name,
      };
    });
    if (bannerMsg) {
      setLinkedBanner(bannerMsg);
      setTimeout(() => setLinkedBanner(null), 4000);
    }
  };

  // Clear one piece of the auto-populated context (e.g. just the contact).
  // Department & any user-typed title/description/metadata stay intact.
  const clearLinkedField = (kind) => {
    setForm(f => {
      const next = { ...f };
      if (kind === 'project') {
        next.project_id = null;
        next._project_name = '';
        // Job # usually came from the project — drop it too.
        next.job_number = '';
      } else if (kind === 'job_number') {
        next.job_number = '';
      } else if (kind === 'ar_code') {
        next.ar_code = '';
      } else if (kind === 'client') {
        next.ar_code = '';
        next.client_name = '';
      } else if (kind === 'contact') {
        next.contact_zoho_id = null;
        next.contact_name = '';
        next.contact_email = '';
        next.contact_phone = '';
      } else if (kind === 'site_address') {
        next.site_address = '';
      }
      return next;
    });
  };

  // Clear every auto-populated link and start fresh.
  // Keeps title/description/department/ticket_type/metadata the user has typed.
  const clearAllLinked = () => {
    setForm(f => ({
      ...f,
      project_id: null, job_number: '', ar_code: '', engagement_id: null,
      client_name: '', contact_zoho_id: null, contact_name: '',
      contact_email: '', contact_phone: '', site_address: '',
      _project_name: '',
    }));
    setLinkedBanner(null);
  };

  // When user picks a search result ────────────────────────────────────────
  const handlePickSearchOption = async (opt) => {
    if (!opt) return;
    let url = null;
    let banner = '';
    if (opt.kind === 'project') {
      url = `/api/tickets/lookup/context?project_id=${opt.project_id}`;
      banner = `Linked to project — ${opt.label}`;
    } else if (opt.kind === 'client') {
      url = `/api/tickets/lookup/context?ar_code=${encodeURIComponent(opt.ar_code)}`;
      banner = `Linked to client — ${opt.label}`;
    } else if (opt.kind === 'engagement') {
      url = `/api/tickets/lookup/context?engagement_id=${opt.engagement_id}`;
      banner = `Linked to engagement — ${opt.label}`;
    } else if (opt.kind === 'contact') {
      url = `/api/tickets/lookup/context?zoho_id=${encodeURIComponent(opt.contact_zoho_id)}`;
      banner = `Linked to contact — ${opt.label}`;
    }
    if (url) {
      const r = await api.get(url);
      applyContext(r.data, banner);
    }
    setSearchInput('');
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('Title is required — enter a short summary at the top.');
      try {
        const body = document.querySelector('.MuiDialogContent-root');
        if (body) body.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (_) { /* no-op */ }
      return;
    }
    if (!form.description || !form.description.trim()) {
      // Mike Murray, 2026-04-24: description is mandatory.
      setError('Description is required — explain what’s happening so whoever picks this up has context.');
      // Scroll the dialog body to the top so the error alert is visible.
      try {
        const body = document.querySelector('.MuiDialogContent-root');
        if (body) body.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (_) { /* no-op */ }
      return;
    }
    setError(''); setSaving(true);
    try {
      const payload = { ...form };
      // Strip UI-only fields
      delete payload._ticket_number;
      delete payload._project_name;
      if (!payload.due_at) payload.due_at = null;
      // On create: strip agent_id (hidden from creator) + let server force Status='New'.
      if (mode !== 'edit') {
        delete payload.agent_id;
        delete payload.status;
      }
      if (!payload.ticket_type) payload.ticket_type = null;

      let t;
      if (mode === 'edit') {
        t = (await api.patch(`/api/tickets/${ticketId}`, payload)).data;
      } else {
        t = (await api.post('/api/tickets', payload)).data;
      }
      onCreated?.(t);
    } catch (e) {
      console.error('Ticket save failed:', e);
      const msg = e?.response?.data?.error
                || e?.response?.data?.detail
                || e?.message
                || 'Failed to save ticket';
      setError(msg);
      // Surface the error near the top so it's not hidden off-screen.
      try {
        const body = document.querySelector('.MuiDialogContent-root');
        if (body) body.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (_) { /* no-op */ }
    } finally {
      setSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const linked = !!(form.project_id || form.ar_code || form.job_number || form.contact_zoho_id);

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
              {mode === 'edit'
                ? <>🎫 {form._ticket_number || `Ticket #${ticketId}`}</>
                : <>🎫 New Ticket</>}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {mode === 'edit' ? 'Edit ticket' : 'Pick a client, project, or contact — the rest fills in automatically.'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <Stack spacing={2}>
            {error      && <Alert severity="error">{error}</Alert>}
            {linkedBanner && <Alert severity="success" icon={<LinkIcon />} sx={{ py: 0.25 }}>{linkedBanner}</Alert>}

            {/* ── Smart search / auto-populate ─────────────────────────── */}
            <Autocomplete
              size="small"
              options={searchOpts}
              loading={searching}
              value={null}
              inputValue={searchInput}
              onInputChange={(_, v) => setSearchInput(v)}
              onChange={(_, v) => handlePickSearchOption(v)}
              filterOptions={(x) => x}  // server-side search; don't filter client-side
              getOptionLabel={renderOptionLabel}
              isOptionEqualToValue={(a, b) => a?.kind === b?.kind && a?.id === b?.id}
              groupBy={(opt) => (KIND_META[opt.kind] || KIND_META.project).label}
              clearOnBlur
              blurOnSelect
              openOnFocus
              autoHighlight
              noOptionsText={searchInput.length < 2 ? 'Start typing to search…' : 'No matches found'}
              renderOption={(props, opt) => {
                const meta = KIND_META[opt.kind] || KIND_META.project;
                const Icon = meta.icon;
                return (
                <Box component="li" {...props} key={`${opt.kind}-${opt.id}`}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
                    <Icon fontSize="small" color={meta.color === 'default' ? 'action' : meta.color} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{opt.label}</Typography>
                      {opt.sublabel && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{opt.sublabel}</Typography>
                      )}
                    </Box>
                    <Chip size="small" label={meta.label} color={meta.color === 'default' ? undefined : meta.color} variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                  </Box>
                </Box>
                );
              }}
              renderInput={(params) => (
                // MUI v9: spread params, then OVERLAY htmlInput.autoComplete
                // to suppress Chrome's address autofill popup (Tom 2026-05-28).
                // We carefully preserve params.slotProps.htmlInput so the
                // Autocomplete's onChange/value/ref wiring stays intact.
                <TextField {...params}
                  label="🔍 Find client, project, job #, or AR code"
                  placeholder="Start typing a project name, Sage job #, AR code, or contact…"
                  slotProps={{
                    ...(params.slotProps || {}),
                    htmlInput: {
                      ...(params.slotProps?.htmlInput || {}),
                      autoComplete: 'new-password',
                    },
                  }}
                />
              )}
            />

            {linked && (
              <Box sx={{ p: 1.5, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                  {form._project_name  && <Chip icon={<WorkIcon />}  label={form._project_name}    color="primary" size="small" onDelete={() => clearLinkedField('project')} />}
                  {form.job_number     && <Chip label={`Job ${form.job_number}`}                   size="small" onDelete={() => clearLinkedField('job_number')} />}
                  {form.ar_code        && <Chip label={`AR ${form.ar_code}`}                       size="small" onDelete={() => clearLinkedField('ar_code')} />}
                  {form.client_name    && <Chip label={form.client_name}                           size="small" variant="outlined" onDelete={() => clearLinkedField('client')} />}
                  {form.contact_name   && <Chip icon={<PersonIcon />} label={form.contact_name}    size="small" variant="outlined" onDelete={() => clearLinkedField('contact')} />}
                  {form.site_address   && <Chip label={form.site_address}                          size="small" variant="outlined" sx={{ maxWidth: 340 }} onDelete={() => clearLinkedField('site_address')} />}
                  <Box sx={{ flex: 1 }} />
                  <Button size="small" color="inherit" onClick={clearAllLinked} sx={{ ml: 'auto', textTransform: 'none' }}>
                    Clear all
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  Click the × on a tag to remove it, or Clear all to start over.
                </Typography>
              </Box>
            )}

            {/* ── Core fields ──────────────────────────────────────────── */}
            <TextField
              autoFocus
              label="Title *"
              value={form.title}
              onChange={set('title')}
              fullWidth
              size="small"
              placeholder="What's the issue?"
            />

            <TextField
              label="Description *"
              value={form.description}
              onChange={set('description')}
              fullWidth
              size="small"
              multiline
              minRows={3}
              required
              placeholder="What’s happening? Steps to reproduce, error message, client impact, etc."
            />

            <Grid container spacing={2}>
              {/* Zoho ticket type — nested / type-ahead searchable. Full row so the
                  long "CATEGORY :: Subtype" labels remain readable. */}
              <Grid item xs={12}>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={meta.ticket_type_options || []}
                  value={(meta.ticket_type_options || []).find(o => o.value === form.ticket_type) || null}
                  onChange={(_, v) => {
                    const newType = v?.value || '';
                    setForm(f => {
                      const map = meta.category_to_department || {};
                      const suggested = v?.category
                        ? (map[String(v.category).toUpperCase()] || 'Other')
                        : '';
                      // Mike Murray, 2026-04-24: auto-select department when the
                      // ticket type changes. We overwrite freely — switching
                      // ticket type also implies re-selecting the owner dept.
                      // This mirrors Zoho's own category ↔ team mapping.
                      const nextDept = suggested || f.department || '';
                      // If department is actually changing, also clear subtype +
                      // metadata the way the Department picker does.
                      const deptChanged = nextDept && nextDept !== f.department;
                      return {
                        ...f,
                        ticket_type: newType,
                        department: nextDept,
                        ...(deptChanged ? { subtype: '', metadata: {} } : {}),
                      };
                    });
                  }}
                  // Show the full "CATEGORY :: Subtype" in the input once a
                  // value is picked so the Zoho identity is never truncated.
                  // The dropdown list still shows just the subtype under each
                  // category header for easier scanning.
                  getOptionLabel={(opt) => opt?.value || opt?.label || ''}
                  isOptionEqualToValue={(a, b) => a?.value === b?.value}
                  groupBy={(opt) => opt?.category || ''}
                  autoHighlight
                  openOnFocus
                  noOptionsText="No matching ticket types"
                  slotProps={{
                    // Widen the options popper so long "CATEGORY :: Subtype"
                    // labels aren't truncated (Mike Murray, 2026-04-24).
                    paper: { sx: { minWidth: 600 } },
                  }}
                  renderOption={(props, opt) => (
                    <Box component="li" {...props} key={opt.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        {/* Subtype is the scannable label; fall back to the
                            full value for single-level entries (e.g., CAD). */}
                        <Typography variant="body2">{opt.label}</Typography>
                        {!opt.subtype && (
                          <Chip size="small" label="Category" variant="outlined" sx={{ fontSize: '0.6rem', height: 18, ml: 'auto' }} />
                        )}
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ticket Type (Zoho) *"
                      placeholder="Type to filter — e.g. 'network', 'programming', 'aftercare…'"
                      helperText="Picks the Zoho category/subtype — the Department below auto-selects to match."
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth size="small"
                  label="Priority"
                  helperText={dueAtTouched
                    ? 'How urgent is this ticket?'
                    : 'How urgent is this ticket? Due date auto-adjusts: Low=2wk, Normal=1wk, High=1d, Urgent=4h.'}
                  value={form.priority}
                  onChange={(e) => {
                    const p = e.target.value;
                    // Mike Murray, 2026-04-24: priority drives the default
                    // due date. Recompute unless the user has manually set
                    // one (dueAtTouched) — their value wins.
                    setForm(f => ({
                      ...f,
                      priority: p,
                      ...(dueAtTouched ? {} : { due_at: defaultDueAtFor(p) }),
                    }));
                  }}
                >
                  {meta.priorities.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth size="small"
                  label="Department"
                  helperText="Owning team — used for The Wire notification"
                  value={form.department}
                  onChange={(e) => {
                    // Clear subtype + metadata when switching departments
                    setForm(f => ({ ...f, department: e.target.value, subtype: '', metadata: {} }));
                  }}
                  SelectProps={{
                    // Widen the menu so full department descriptions are visible
                    // regardless of the trigger column width (Mike Murray, 2026-04-24).
                    MenuProps: {
                      PaperProps: { sx: { maxWidth: 520, minWidth: 360 } },
                    },
                    renderValue: (selected) => {
                      if (!selected) return <em style={{ color: '#999' }}>— none —</em>;
                      const cfg = (meta.department_configs || []).find(d => d.key === selected);
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: cfg?.color || '#616161' }} />
                          <span>{cfg?.label || selected}</span>
                        </Box>
                      );
                    },
                  }}
                >
                  <MenuItem value=""><em>— none —</em></MenuItem>
                  {(meta.department_configs && meta.department_configs.length > 0
                    ? meta.department_configs
                    : meta.departments.map(d => ({ key: d, label: d, color: '#616161' }))
                  ).map(d => (
                    <MenuItem key={d.key} value={d.key} sx={{ py: 1, alignItems: 'flex-start', whiteSpace: 'normal' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: d.color || '#616161', flexShrink: 0, mt: 0.6 }} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{d.label}</Typography>
                          {d.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'normal', lineHeight: 1.3, mt: 0.25 }}>
                              {d.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {/* Status is edit-only — Mike's rule: status is not shown when creating. */}
              {mode === 'edit' && (
                <Grid item xs={12}>
                  <TextField
                    select fullWidth size="small"
                    label="Status"
                    helperText="New → Open → In Progress → Waiting → Resolved → Closed"
                    value={form.status}
                    onChange={set('status')}
                  >
                    {meta.statuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </TextField>
                </Grid>
              )}
            </Grid>

            {/* Department-specific subtype + extra fields */}
            {deptConfig && (
              <Box sx={{
                p: 1.75,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: (deptConfig.color || '#616161') + '0F',  // ~6% alpha
                borderLeft: '4px solid ' + (deptConfig.color || '#616161'),
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: deptConfig.color || '#616161', flexShrink: 0 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: deptConfig.color }}>
                    {deptConfig.label} ticket
                  </Typography>
                  {deptConfig.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      — {deptConfig.description}
                    </Typography>
                  )}
                </Box>

                <Grid container spacing={1.5}>
                  {/* Subtype dropdown */}
                  {deptConfig.subtypes && deptConfig.subtypes.length > 0 && (
                    <Grid item xs={12}>
                      <TextField
                        select fullWidth size="small"
                        label="Category"
                        value={form.subtype || ''}
                        onChange={set('subtype')}
                      >
                        <MenuItem value=""><em>— none —</em></MenuItem>
                        {deptConfig.subtypes.map(s => (
                          <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}

                  {/* Dynamic department-specific fields */}
                  {deptConfig.fields && deptConfig.fields.map(field => {
                    const val = (form.metadata && form.metadata[field.key]) ?? '';
                    const setField = (newVal) => setForm(f => ({
                      ...f,
                      metadata: { ...(f.metadata || {}), [field.key]: newVal },
                    }));
                    const common = { fullWidth: true, size: 'small', label: field.label, value: val };

                    if (field.type === 'select') {
                      return (
                        <Grid item xs={12} sm={6} key={field.key}>
                          <TextField {...common} select onChange={(e) => setField(e.target.value)}>
                            <MenuItem value=""><em>—</em></MenuItem>
                            {(field.options || []).map(o => (
                              <MenuItem key={o} value={o}>{o}</MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      );
                    }
                    if (field.type === 'textarea') {
                      return (
                        <Grid item xs={12} key={field.key}>
                          <TextField {...common} multiline minRows={2} onChange={(e) => setField(e.target.value)} />
                        </Grid>
                      );
                    }
                    if (field.type === 'boolean') {
                      return (
                        <Grid item xs={12} sm={6} key={field.key}>
                          <TextField {...common} select onChange={(e) => setField(e.target.value)}>
                            <MenuItem value=""><em>—</em></MenuItem>
                            <MenuItem value="yes">Yes</MenuItem>
                            <MenuItem value="no">No</MenuItem>
                          </TextField>
                        </Grid>
                      );
                    }
                    if (field.type === 'number') {
                      return (
                        <Grid item xs={12} sm={6} key={field.key}>
                          <TextField {...common} type="number" onChange={(e) => setField(e.target.value)} />
                        </Grid>
                      );
                    }
                    if (field.type === 'money') {
                      return (
                        <Grid item xs={12} sm={6} key={field.key}>
                          <TextField
                            {...common}
                            type="number"
                            InputProps={{ startAdornment: <Box sx={{ mr: 0.5, color: 'text.secondary' }}>$</Box> }}
                            onChange={(e) => setField(e.target.value)}
                          />
                        </Grid>
                      );
                    }
                    if (field.type === 'date') {
                      return (
                        <Grid item xs={12} sm={6} key={field.key}>
                          <TextField
                            {...common}
                            type="date"
                            slotProps={{ inputLabel: { shrink: true } }}
                            onChange={(e) => setField(e.target.value)}
                          />
                        </Grid>
                      );
                    }
                    // Default: text
                    return (
                      <Grid item xs={12} sm={6} key={field.key}>
                        <TextField {...common} onChange={(e) => setField(e.target.value)} />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Cross-references (auto-filled — override if needed)</Typography></Divider>

            <Grid container spacing={2}>
              {/* Job # — narrower, always shown */}
              <Grid item xs={12} sm={mode === 'edit' ? 4 : 6}>
                <TextField fullWidth size="small" label="Job #" value={form.job_number || ''} onChange={set('job_number')} />
              </Grid>
              {/* AR Code — edit only. Mike Murray (2026-04-24): remove from create dialog. */}
              {mode === 'edit' && (
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="AR Code" value={form.ar_code || ''} onChange={set('ar_code')} />
                </Grid>
              )}
              <Grid item xs={12} sm={mode === 'edit' ? 4 : 6}>
                <TextField fullWidth size="small" label="Client Name" value={form.client_name || ''} onChange={set('client_name')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Contact Name" value={form.contact_name || ''} onChange={set('contact_name')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Contact Email" value={form.contact_email || ''} onChange={set('contact_email')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Contact Phone" value={form.contact_phone || ''} onChange={set('contact_phone')} />
              </Grid>
              {/* Site Address — smart picker.
                  • No client selected: plain free text.
                  • Client with exactly one site: auto-populated (see useEffect above), still editable.
                  • Client with multiple sites: Autocomplete restricted to that client's sites,
                    with free-text override for new/one-off addresses. */}
              <Grid item xs={12} sm={6}>
                {clientSites.length > 1 ? (
                  <Autocomplete
                    size="small"
                    freeSolo
                    options={clientSites}
                    getOptionLabel={(opt) => typeof opt === 'string' ? opt : (opt?.site_address || '')}
                    isOptionEqualToValue={(a, b) => (typeof a === 'string' ? a : a?.site_address) === (typeof b === 'string' ? b : b?.site_address)}
                    value={form.site_address || ''}
                    onChange={(_, v) => {
                      const addr = typeof v === 'string' ? v : (v?.site_address || '');
                      setForm(f => ({ ...f, site_address: addr }));
                    }}
                    onInputChange={(_, v, reason) => {
                      // Keep typed overrides flowing through.
                      if (reason === 'input') setForm(f => ({ ...f, site_address: v }));
                    }}
                    loading={sitesLoading}
                    renderOption={(props, opt) => (
                      <Box component="li" {...props} key={`${opt.project_id}-${opt.site_address}`}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" noWrap>{opt.site_address}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {[opt.project_name, opt.job_number ? `Job ${opt.job_number}` : null].filter(Boolean).join(' — ')}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Site Address"
                        helperText={`${clientSites.length} sites on file — pick one or type a new address`}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    fullWidth size="small"
                    label="Site Address"
                    value={form.site_address || ''}
                    onChange={set('site_address')}
                    helperText={
                      clientSites.length === 1 ? 'Auto-filled from client — edit if this ticket is for a different address'
                      : sitesLoading ? 'Loading client sites…'
                      : undefined
                    }
                  />
                )}
              </Grid>
            </Grid>

            <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Assignment</Typography></Divider>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={mode === 'edit' ? 4 : 6}>
                <TextField
                  select fullWidth size="small"
                  label="Assigned To"
                  helperText="Ticket owner (primary responsibility)"
                  value={form.assigned_to || ''}
                  onChange={(e) => setForm(f => ({ ...f, assigned_to: e.target.value || null }))}
                >
                  <MenuItem value=""><em>— unassigned —</em></MenuItem>
                  {users.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
                </TextField>
              </Grid>
              {/* AGENT — edit mode only. Not shown during ticket creation (Mike's rule). */}
              {mode === 'edit' && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    select fullWidth size="small"
                    label="Agent"
                    helperText="Person actively working the ticket"
                    value={form.agent_id || ''}
                    onChange={(e) => setForm(f => ({ ...f, agent_id: e.target.value || null }))}
                  >
                    <MenuItem value=""><em>— no agent —</em></MenuItem>
                    {users.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
                  </TextField>
                </Grid>
              )}
              <Grid item xs={12} sm={mode === 'edit' ? 4 : 6}>
                <TextField
                  fullWidth size="small" label="Due Date"
                  type="datetime-local"
                  value={form.due_at || ''}
                  onChange={(e) => {
                    // Once the user edits the due date, stop auto-syncing it
                    // to priority changes.
                    setDueAtTouched(true);
                    setForm(f => ({ ...f, due_at: e.target.value }));
                  }}
                  helperText={dueAtTouched || mode === 'edit'
                    ? undefined
                    : 'Default from priority — edit to override.'}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
            </Grid>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            {linked && <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>✓ Auto-populated from {form._project_name ? 'project' : form.contact_name ? 'contact' : 'lookup'}</Typography>}
          </Box>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || loading} sx={{ ml: 1 }}>
            {saving ? 'Saving…' : (mode === 'edit' ? 'Save Changes' : 'Create Ticket')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export { PriorityChip };
